import React, { useState, useRef, useEffect, useContext } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Badge,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Send as SendIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import config from "../config";

const Chatbot = ({ dashboardData, onDashboardUpdate }) => {
  const theme = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("connected");
  const [conversationContext, setConversationContext] = useState([]);
  const [suggestedQueries, setSuggestedQueries] = useState([]);
  const [chartPreview, setChartPreview] = useState(null);
  const [showChartDialog, setShowChartDialog] = useState(false);
  const messagesEndRef = useRef(null);
  const { API_BASE_URL } = config;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (
      !isOpen &&
      messages.length > 0 &&
      messages[messages.length - 1]?.role === "assistant"
    ) {
      setUnreadCount((prev) => prev + 1);
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (dashboardData && isOpen) {
      const suggestions = generateSmartSuggestions(dashboardData);
      setSuggestedQueries(suggestions);
    }
  }, [dashboardData, isOpen]);

  const generateSmartSuggestions = (data) => {
    const suggestions = [];

    if (data.totalRevenue) {
      suggestions.push("Show my revenue analysis chart");
      suggestions.push("Analyze my profit margins");
    }

    if (data.totalAssets && data.totalLiabilities) {
      suggestions.push("Create balance sheet chart");
      suggestions.push("Calculate my debt-to-equity ratio");
    }

    if (data.totalExpenses) {
      suggestions.push("Show expense breakdown chart");
      suggestions.push("Analyze my expense categories");
    }

    suggestions.push("Show cash flow analysis");
    suggestions.push("Create financial dashboard overview");

    return suggestions.slice(0, 4);
  };

  const sendMessage = async (messageText = null) => {
    const messageToSend = messageText || input.trim();
    if (!messageToSend || loading) return;

    const userMessage = {
      role: "user",
      content: messageToSend,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setError(null);

    const newContext = [...conversationContext, userMessage].slice(-10);
    setConversationContext(newContext);

    try {
      setLoading(true);
      setConnectionStatus("connecting");

      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");

      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const payload = {
        message: messageToSend,
        dashboardData: dashboardData || null,
        context: newContext,
      };

      const res = await axios.post(`${API_BASE_URL}/api/v1/chat`, payload, {
        headers,
        timeout: 30000,
      });

      setConnectionStatus("connected");

      if (res.data.success && res.data.data) {
        const botMessage = {
          role: "assistant",
          content:
            res.data.data.response ||
            "I received your message but couldn't generate a response.",
          timestamp: res.data.data.timestamp || new Date().toISOString(),
          username: res.data.data.username,
          isDashboardInteraction: res.data.data.isDashboardInteraction,
          actions: res.data.data.actions,
          calculatedRatios: res.data.data.calculatedRatios,
        };

        setMessages((prev) => [...prev, botMessage]);
        setConversationContext((prev) => [...prev, botMessage].slice(-10));

        if (res.data.data.actions) {
          handleBotActions(res.data.data.actions, botMessage);
        }

        if (res.data.data.calculatedRatios) {
          displayFinancialRatios(res.data.data.calculatedRatios);
        }
      } else {
        throw new Error(res.data.error || "Invalid response format");
      }
    } catch (error) {
      console.error("Chat error:", error);
      setConnectionStatus("error");

      let errorMessage =
        "I'm having trouble connecting right now. Please try again.";

      if (error.response) {
        const statusCode = error.response.status;
        const serverError = error.response.data?.error;

        switch (statusCode) {
          case 400:
            errorMessage =
              serverError || "Please check your message and try again.";
            break;
          case 401:
            errorMessage = "Authentication required. Please log in again.";
            break;
          case 403:
            errorMessage = "Access denied. Please check your permissions.";
            break;
          case 429:
            errorMessage =
              "Too many requests. Please wait a moment before trying again.";
            break;
          case 500:
            errorMessage = "Server error. Our team has been notified.";
            break;
          default:
            errorMessage =
              serverError ||
              `Connection error (${statusCode}). Please try again.`;
        }
      } else if (error.request) {
        errorMessage =
          "Network connection failed. Please check your internet connection.";
      } else if (error.code === "ECONNABORTED") {
        errorMessage = "Request timed out. Please try again.";
      }

      const errorBotMessage = {
        role: "assistant",
        content: errorMessage,
        timestamp: new Date().toISOString(),
        isError: true,
      };

      setMessages((prev) => [...prev, errorBotMessage]);
      setError(errorMessage);
    } finally {
      setLoading(false);
      setTimeout(() => setConnectionStatus("connected"), 2000);
    }
  };

  const handleBotActions = (actions, botMessage) => {
    switch (actions.type) {
      case "chart_interaction":
        setChartPreview({
          chartName: actions.chartName,
          chartType: actions.chartType,
          action: actions.action,
          data: actions.data,
          description: actions.description,
          message: botMessage.content,
        });
        setShowChartDialog(true);
        break;

      case "modify_data":
        if (actions.field && actions.value !== undefined && onDashboardUpdate) {
          const updateData = {
            [actions.field]: actions.value,
          };

          onDashboardUpdate(updateData);

          // Show success message
          const successMessage = {
            role: "assistant",
            content: `✅ Updated ${actions.field
              .replace(/([A-Z])/g, " $1")
              .toLowerCase()} to ₹${actions.value.toLocaleString("en-IN")}`,
            timestamp: new Date().toISOString(),
            isSuccess: true,
          };

          setMessages((prev) => [...prev, successMessage]);
        } else {
          console.error("Invalid update data:", actions);

          const errorMessage = {
            role: "assistant",
            content:
              "❌ Sorry, I couldn't process that update. Please try again with a clear format like 'set revenue to 100000'",
            timestamp: new Date().toISOString(),
            isError: true,
          };

          setMessages((prev) => [...prev, errorMessage]);
        }
        break;

      case "show_analysis":
        break;

      case "create_chart":
        break;

      default:
    }
  };

  const displayFinancialRatios = (ratios) => {
    // Add ratios as a special message type
    const ratioMessage = {
      role: "assistant",
      content: "Here are your calculated financial ratios:",
      timestamp: new Date().toISOString(),
      isRatioDisplay: true,
      ratios: ratios,
    };

    setMessages((prev) => [...prev, ratioMessage]);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !loading && input.trim()) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearMessages = () => {
    setMessages([]);
    setError(null);
    setUnreadCount(0);
    setConversationContext([]);
    setSuggestedQueries([]);
  };

  const retryLastMessage = () => {
    if (messages.length >= 2) {
      const lastUserMessage = messages[messages.length - 2];
      if (lastUserMessage.role === "user") {
        setInput(lastUserMessage.content);
        setMessages((prev) => prev.slice(0, -2));
        setConversationContext((prev) => prev.slice(0, -2));
      }
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case "connected":
        return theme.palette.success.main;
      case "connecting":
        return theme.palette.warning.main;
      case "error":
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getChartIcon = (chartType) => {
    switch (chartType) {
      case "pie":
        return <PieChartIcon />;
      case "line":
        return <LineChartIcon />;
      case "bar":
        return <BarChartIcon />;
      default:
        return <AnalyticsIcon />;
    }
  };

  const renderMessage = (msg, i) => {
    if (msg.isRatioDisplay && msg.ratios) {
      return (
        <Box sx={{ mb: 2 }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              backgroundColor: theme.palette.info.light,
              borderRadius: 2,
              border: `1px solid ${theme.palette.info.main}`,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
              📊 Financial Ratios Analysis
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {Object.entries(msg.ratios).map(([key, value]) => (
                <Box
                  key={key}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography
                    variant="caption"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {key.replace(/([A-Z])/g, " $1").toLowerCase()}:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
          mb: 1,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            maxWidth: "85%",
            backgroundColor: msg.isError
              ? theme.palette.error.light
              : msg.role === "user"
              ? theme.palette.primary.main
              : theme.palette.background.paper,
            color: msg.isError
              ? theme.palette.error.contrastText
              : msg.role === "user"
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
            borderRadius: 2,
            borderBottomRightRadius: msg.role === "user" ? 0.5 : 2,
            borderBottomLeftRadius: msg.role === "user" ? 2 : 0.5,
            border: msg.isError
              ? `1px solid ${theme.palette.error.main}`
              : "none",
          }}
        >
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {msg.content}
          </Typography>

          {/* Show dashboard interaction indicator */}
          {msg.isDashboardInteraction && (
            <Chip
              icon={<DashboardIcon />}
              label="Dashboard"
              size="small"
              sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
            />
          )}

          {msg.timestamp && (
            <Typography
              variant="caption"
              sx={{
                opacity: 0.7,
                fontSize: "0.7rem",
                mt: 0.5,
                display: "block",
              }}
            >
              {new Date(msg.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
      }}
    >
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ mb: 10 }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
          action={
            <IconButton
              size="small"
              aria-label="retry"
              color="inherit"
              onClick={retryLastMessage}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        >
          Connection Error
        </Alert>
      </Snackbar>

      {/* Chart Preview Dialog */}
      <Dialog
        open={showChartDialog}
        onClose={() => setShowChartDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {chartPreview && getChartIcon(chartPreview.chartType)}
            {chartPreview?.description || "Chart Preview"}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {chartPreview?.message}
          </Typography>
          {chartPreview && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Suggested Data Points:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {chartPreview.data?.map((point, idx) => (
                  <Chip
                    key={idx}
                    label={point.replace(/_/g, " ")}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChartDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
           
            
              setShowChartDialog(false);
            }}
          >
            {chartPreview?.action === "create"
              ? "Create Chart"
              : "Update Chart"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            style={{ marginBottom: 16 }}
          >
            <Paper
              elevation={8}
              sx={{
                width: 380,
                height: 520,
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: theme.palette.primary.contrastText,
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    F-ai Assistant
                  </Typography>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: getConnectionStatusColor(),
                      animation:
                        connectionStatus === "connecting"
                          ? "pulse 1.5s infinite"
                          : "none",
                      "@keyframes pulse": {
                        "0%": { opacity: 1 },
                        "50%": { opacity: 0.5 },
                        "100%": { opacity: 1 },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Clear conversation">
                    <IconButton
                      size="small"
                      onClick={clearMessages}
                      sx={{ color: "inherit", opacity: 0.8 }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close chat">
                    <IconButton
                      size="small"
                      onClick={() => setIsOpen(false)}
                      sx={{ color: "inherit", opacity: 0.8 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Suggestions */}
              {suggestedQueries.length > 0 && messages.length === 0 && (
                <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                  <Typography
                    variant="caption"
                    sx={{ mb: 1, display: "block", opacity: 0.7 }}
                  >
                    💡 Try asking:
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {suggestedQueries.map((suggestion, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        variant="outlined"
                        onClick={() => handleSuggestionClick(suggestion)}
                        sx={{
                          fontSize: "0.75rem",
                          textTransform: "none",
                          justifyContent: "flex-start",
                          borderColor: theme.palette.primary.light,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {messages.length === 0 && suggestedQueries.length === 0 && (
                  <Box
                    sx={{
                      textAlign: "center",
                      color: theme.palette.text.secondary,
                      mt: 4,
                      p: 2,
                    }}
                  >
                    <ChatIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Hi! I'm F-ai, your financial assistant.
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Ask me about your finances, create charts, or analyze your
                      data.
                    </Typography>
                  </Box>
                )}

                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {renderMessage(msg, i)}
                  </motion.div>
                ))}

                {loading && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          borderBottomLeftRadius: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={16} />
                        <Typography
                          variant="body2"
                          color={theme.palette.text.secondary}
                        >
                          F-ai is thinking...
                        </Typography>
                      </Paper>
                    </Box>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* Input */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: theme.palette.background.paper,
                  borderTop: 1,
                  borderColor: theme.palette.grey[200],
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Ask about charts, analysis, or financial data..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  multiline
                  maxRows={3}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                    },
                  }}
                />
                <Tooltip title={loading ? "Sending..." : "Send message"}>
                  <span>
                    <IconButton
                      onClick={() => sendMessage()}
                      disabled={loading || !input.trim()}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        "&.Mui-disabled": {
                          backgroundColor: theme.palette.grey[300],
                          color: theme.palette.grey[500],
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SendIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={9}
          invisible={unreadCount === 0}
        >
          <Fab
            onClick={() => setIsOpen(!isOpen)}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: theme.palette.primary.contrastText,
              width: 64,
              height: 64,
              fontSize: "14px",
              fontWeight: "bold",
              boxShadow: theme.shadows[6],
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: theme.shadows[8],
              },
            }}
          >
            {isOpen ? <CloseIcon /> : "F-ai"}
          </Fab>
        </Badge>
      </motion.div>
    </Box>
  );
};

export default Chatbot;
